import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, TemplateRef, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import Swal from 'sweetalert2';
import { ChatService, getChatErrorDetail } from '../../../services/chat.service';
import { ChatRealtimeService } from '../../../services/chat-realtime.service';
import { VoiceRecorderService } from '../../../services/voice-recorder.service';
import { environment } from '../../../../environments/environment';
import { Helpers } from '../../../helpers/helpers';
import { ChatRoomDTO } from '../../../models/chat-room-dto';
import { ChatFileDTO, ChatMessageDTO } from '../../../models/chat-message-dto';
import { ChatMessagesReadByAllDTO } from '../../../models/chat-messages-read-by-all-dto';
import { ChatManageMembersModalComponent } from '../chat-manage-members-modal/chat-manage-members-modal.component';

const PAGE_SIZE = 100;
// Por debajo de este scrollTop (px) se considera que el usuario ha llegado arriba del todo.
const LOAD_MORE_THRESHOLD_PX = 50;
// A menos de esta distancia (px) del final se considera que el usuario sigue "pegado" abajo.
const STICK_TO_BOTTOM_THRESHOLD_PX = 30;
// Sin .svg a propósito (CHAT_REVISION.md C10): el backend ya no lo sirve con Content-Type de imagen
// (puede llevar <script> embebido), así que previsualizarlo aquí no funcionaría de todas formas.
const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
// Mismo valor que ChatLimits.MaxAttachmentBytes en el backend (que es quien lo valida de verdad): si
// se cambia allí, cambiarlo también aquí. Aquí solo sirve para avisar antes de subir nada.
const MAX_ATTACHMENT_MB = 25;
const MAX_ATTACHMENT_BYTES = MAX_ATTACHMENT_MB * 1024 * 1024;
// Mismo valor que ChatLimits.MaxVoiceAudioMegabytes en el backend: si se cambia allí, cambiarlo aquí.
// Con el límite de grabación actual (30 s ≈ 1 MB de WAV) no debería saltar nunca.
const MAX_VOICE_AUDIO_MB = 5;
const MAX_VOICE_AUDIO_BYTES = MAX_VOICE_AUDIO_MB * 1024 * 1024;

type VoiceState = 'idle' | 'preparing' | 'recording' | 'processing';

@Component({
  selector: 'app-chat-conversation',
  standalone: true,
  imports: [FormsModule, DatePipe],
  templateUrl: './chat-conversation.component.html',
  styleUrl: './chat-conversation.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChatConversationComponent implements OnChanges, OnDestroy {
  @Input() room: ChatRoomDTO | null = null;
  @Output() roomLeft = new EventEmitter<void>();
  // Solo se pinta en móvil (chat-conversation.component.scss): vuelve a la lista de salas sin cerrar
  // la conversación, que sigue activa por debajo (mismo estado, solo cambia qué panel se ve).
  @Output() back = new EventEmitter<void>();

  @ViewChild('messagesContainer') messagesContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;
  @ViewChild('previewModal') previewModal?: TemplateRef<unknown>;

  messages: ChatMessageDTO[] = [];
  newMessage = '';
  loadingOlder = false;
  hasMoreOlder = true;
  sending = false;

  editingMessageId: string | null = null;
  editingText = '';

  editingTitle = false;
  editingTitleText = '';
  savingTitle = false;

  /** Archivo elegido con el explorador de Windows, pendiente de enviar junto al siguiente mensaje. */
  selectedFile: File | null = null;

  /**
   * Ha llegado algún mensaje nuevo mientras el usuario leía más arriba (sin moverle, ver
   * onMessageReceived): muestra el botón "Nuevos mensajes". Se apaga al volver al final por cualquier
   * vía (el botón, el propio scroll del usuario, o scrollToBottom al enviar) y al cambiar de sala.
   */
  hasNewBelow = false;

  previewFileName = '';
  previewFileUri = '';
  previewFileSize = 0;

  /**
   * Mensaje de voz (WHISPER.md §5), con overlay a pantalla completa en todos los estados salvo 'idle':
   * 'preparing' mientras el micrófono arranca (un clic cancela), 'recording' mientras graba (un clic en
   * cualquier sitio para y envía), 'processing' mientras se convierte y el backend transcribe y envía.
   */
  voiceState: VoiceState = 'idle';
  voiceSeconds = 0;
  readonly voiceSupported: boolean;
  private voiceRoomId: string | null = null;
  private voiceStartedAt = 0;
  private voiceTickTimer?: ReturnType<typeof setInterval>;
  private voiceLimitTimer?: ReturnType<typeof setTimeout>;
  // La conversión del audio es asíncrona y puede terminar con el chat ya cerrado: detectChanges()
  // sobre una vista destruida lanza error.
  private destroyed = false;

  // Mientras el propio código mueve scrollTop (al pintar la última línea o al restaurar la
  // posición tras cargar mensajes antiguos), el navegador dispara igualmente el evento "scroll"
  // nativo — sin esta bandera, onScroll() lo confundiría con un gesto real del usuario y
  // encadenaría cargas de "mensajes anteriores" en bucle nada más abrir la sala.
  private suppressScrollEvents = false;

  // true mientras la vista está pegada al final (al abrir la sala, tras enviar, o si el propio
  // usuario ha bajado hasta abajo). Las imágenes adjuntas cargan DESPUÉS de scrollToBottom() y, al
  // no tener alto reservado, empujan el contenido hacia abajo dejando la vista por encima del final:
  // onImageLoaded() usa esta bandera para volver a bajar, pero solo si el usuario no se ha movido
  // de ahí (si ha subido a leer el histórico, no se le debe arrastrar).
  private stickToBottom = true;

  private modalService = inject(NgbModal);
  private chatService = inject(ChatService);
  chatRealtimeService = inject(ChatRealtimeService);
  private helpers = inject(Helpers);
  private voiceRecorder = inject(VoiceRecorderService);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private realtimeSubscriptions = new Subscription();

  // Carga de mensajes en vuelo (la inicial de la sala o una página de mensajes anteriores). Se cancela
  // al cambiar de sala (onRoomChanged): cancelar una petición de HttpClient la aborta, así que su
  // respuesta nunca llega a pintarse en la sala nueva (CHAT_REVISION.md B9).
  private messagesRequest?: Subscription;

  constructor() {
    this.voiceSupported = this.voiceRecorder.isSupported();
    this.realtimeSubscriptions.add(
      this.chatRealtimeService.messageReceived$.subscribe(message => this.onMessageReceived(message))
    );
    this.realtimeSubscriptions.add(
      this.chatRealtimeService.messagesReadByAll$.subscribe(event => this.onMessagesReadByAll(event))
    );
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.realtimeSubscriptions.unsubscribe();
    this.messagesRequest?.unsubscribe();
    // Cerrar el chat a mitad de grabación: se descarta y se libera el micrófono.
    this.cancelVoiceRecording();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['room']) return;

    const previous: ChatRoomDTO | null = changes['room'].previousValue ?? null;
    const current: ChatRoomDTO | null = changes['room'].currentValue ?? null;

    // Cambios de la misma sala (p. ej. tras notifyRoomsChanged, que llega como un objeto nuevo con
    // el mismo IdChat) no deben recargar los mensajes ni el scroll.
    if (current?.IdChat === previous?.IdChat) return;

    this.onRoomChanged(current);
  }

  private onRoomChanged(room: ChatRoomDTO | null): void {
    // Todo lo que queda en vuelo o a medias es de la sala anterior: una carga de mensajes sin
    // terminar se cancela (si no, pintaría sus mensajes aquí y marcaría leída la otra sala), y se
    // reinician el "cargando anteriores" y el modo edición.
    this.messagesRequest?.unsubscribe();
    this.messages = [];
    this.hasMoreOlder = true;
    this.loadingOlder = false;
    this.hasNewBelow = false;
    this.cancelEdit();
    this.cancelEditTitle();
    this.cancelVoiceRecording();

    if (!room) {
      this.cdr.detectChanges();
      return;
    }

    this.messagesRequest = this.chatService.getMessages(room.IdChat, undefined, PAGE_SIZE).subscribe({
      next: messages => {
        this.messages = messages;
        this.hasMoreOlder = messages.length === PAGE_SIZE;
        this.cdr.detectChanges();
        this.scrollToBottom();
        this.markAsRead(room.IdChat);
      },
      error: err => this.helpers.showError('Error al recuperar los mensajes', getChatErrorDetail(err))
    });
  }

  onScroll(): void {
    if (this.suppressScrollEvents) return;

    const el = this.messagesContainer?.nativeElement;
    if (!el) return;

    // Gesto real del usuario (los scrolls programáticos se descartan arriba): ¿se ha quedado al
    // final de la conversación o ha subido a leer?
    this.stickToBottom = el.scrollHeight - el.scrollTop - el.clientHeight < STICK_TO_BOTTOM_THRESHOLD_PX;
    // Ha bajado él mismo hasta el final: ya ha visto lo nuevo.
    if (this.stickToBottom) this.hasNewBelow = false;

    if (!this.room || this.loadingOlder || !this.hasMoreOlder) return;

    if (el.scrollTop < LOAD_MORE_THRESHOLD_PX) {
      this.loadOlderMessages(this.room.IdChat);
    }
  }

  private loadOlderMessages(idChat: string): void {
    if (this.messages.length === 0) return;

    const el = this.messagesContainer!.nativeElement;
    const previousScrollHeight = el.scrollHeight;
    const previousScrollTop = el.scrollTop;
    const oldestMessageId = this.messages[0].IdMessage;

    this.loadingOlder = true;

    this.messagesRequest = this.chatService.getMessages(idChat, oldestMessageId, PAGE_SIZE).subscribe({
      next: olderMessages => {
        this.hasMoreOlder = olderMessages.length === PAGE_SIZE;
        this.messages = [...olderMessages, ...this.messages];
        this.loadingOlder = false;

        // detectChanges() actualiza el DOM al momento (Angular con OnPush no espera al siguiente
        // ciclo); leer scrollHeight justo después fuerza al navegador a recalcular el layout ya
        // mismo, así que no hace falta esperar a ningún frame para tener el valor correcto.
        this.suppressScrollEvents = true;
        this.cdr.detectChanges();

        // Mantener la posición visual: el contenido nuevo se añade arriba, así que compensamos
        // el alto que ha crecido, sumando el scrollTop que ya tenía (no siempre es 0 — el umbral
        // de disparo es <50px, no exactamente 0) para que el usuario no note ningún salto.
        el.scrollTop = el.scrollHeight - previousScrollHeight + previousScrollTop;
        this.reenableScrollEventsSoon();
      },
      error: err => {
        this.loadingOlder = false;
        this.helpers.showError('Error al recuperar mensajes anteriores', getChatErrorDetail(err));
      }
    });
  }

  send(): void {
    const text = this.newMessage.trim();
    if ((!text && !this.selectedFile) || this.sending || this.voiceState !== 'idle' || !this.room) return;

    this.sending = true;
    const idChat = this.room.IdChat;
    const file = this.selectedFile ?? undefined;

    this.chatService.sendMessage(idChat, text, file).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: message => {
        this.upsertMessage(message);
        this.newMessage = '';
        this.removeSelectedFile();
        this.sending = false;
        this.cdr.detectChanges();
        this.scrollToBottom();
        // Para que la lista de salas muestre el último mensaje/preview al instante.
        this.chatService.notifyRoomsChanged();
      },
      error: err => {
        this.sending = false;
        this.helpers.showError('Error al enviar el mensaje', getChatErrorDetail(err));
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Botón de micrófono: abre el micrófono con el overlay en "Preparando micrófono..." y, cuando ya da
   * audio real (tarda 1-2 s), empieza a grabar y pasa a "Grabando" — así el usuario no habla antes de
   * tiempo y no se pierde el principio del mensaje.
   */
  async startVoiceRecording(): Promise<void> {
    if (!this.room || this.sending || this.voiceState !== 'idle') return;

    // La sala se fija al empezar: el mensaje va a la sala en la que se grabó.
    this.voiceRoomId = this.room.IdChat;
    this.voiceState = 'preparing';
    this.cdr.detectChanges();

    let ready: boolean;
    try {
      ready = (await this.voiceRecorder.open()) && (await this.voiceRecorder.waitUntilReady());
    } catch {
      // Ya cancelado por el usuario (p. ej. deniega el permiso después de cancelar): nada que avisar.
      if (this.voiceState !== 'preparing') return;
      this.voiceRecorder.cancel();
      this.endVoiceMessage();
      this.helpers.showError('No se ha podido acceder al micrófono', 'Comprueba que hay un micrófono conectado y que el navegador tiene permiso para usarlo.');
      return;
    }

    // Cancelado durante la preparación (clic en el overlay, cambio de sala o cierre del chat).
    if (!ready || this.voiceState !== 'preparing' || this.destroyed) return;

    this.voiceRecorder.startRecording();
    this.voiceState = 'recording';
    this.voiceSeconds = 0;
    this.voiceStartedAt = Date.now();
    this.voiceTickTimer = setInterval(() => {
      const seconds = Math.floor((Date.now() - this.voiceStartedAt) / 1000);
      if (seconds === this.voiceSeconds) return;
      this.voiceSeconds = seconds;
      this.cdr.detectChanges();
    }, 200);
    // Límite de duración: se para y se envía solo, igual que con un clic.
    this.voiceLimitTimer = setTimeout(() => this.stopVoiceRecording(), environment.voiceMaxRecordingSeconds * 1000);
    this.cdr.detectChanges();
  }

  /** Clic en el overlay: durante la preparación cancela; durante la grabación para y envía. */
  onVoiceOverlayClick(): void {
    if (this.voiceState === 'preparing') this.cancelVoiceRecording();
    else if (this.voiceState === 'recording') void this.stopVoiceRecording();
  }

  /** Clic en cualquier sitio del overlay, o límite de duración: para la grabación y la envía. */
  async stopVoiceRecording(): Promise<void> {
    if (this.voiceState !== 'recording' || !this.voiceRoomId) return;

    const idChat = this.voiceRoomId;
    this.clearVoiceTimers();
    this.voiceState = 'processing';
    this.cdr.detectChanges();

    let recording;
    try {
      recording = await this.voiceRecorder.stop();
      if (this.destroyed) return;
    } catch {
      this.endVoiceMessage();
      this.helpers.showError('Error al procesar el audio', 'No se ha podido procesar la grabación, inténtalo de nuevo o escribe el mensaje.');
      return;
    }

    // Sin llamar al backend: Whisper, con silencio, suele inventarse una frase en vez de devolver
    // un texto vacío (WHISPER.md §5.2).
    if (!recording.hasVoice) {
      this.endVoiceMessage();
      this.helpers.showError('No se ha detectado voz', 'Asegúrate de hablar cerca del micrófono e inténtalo de nuevo.');
      return;
    }

    if (recording.wav.size > MAX_VOICE_AUDIO_BYTES) {
      this.endVoiceMessage();
      this.helpers.showError('Audio demasiado grande', `El tamaño máximo permitido es de ${MAX_VOICE_AUDIO_MB} MB.`);
      return;
    }

    this.chatService.sendVoiceMessage(idChat, recording.wav).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: message => {
        this.upsertMessage(message);
        this.endVoiceMessage();
        this.scrollToBottom();
        this.chatService.notifyRoomsChanged();
      },
      error: err => {
        this.endVoiceMessage();
        this.helpers.showError('Error al enviar el mensaje de voz', getChatErrorDetail(err));
      }
    });
  }

  /**
   * Descarta una preparación o grabación en curso (clic durante la preparación, cambio de sala, cierre
   * del chat). Un envío ya en marcha ('processing') sigue.
   */
  private cancelVoiceRecording(): void {
    if (this.voiceState !== 'preparing' && this.voiceState !== 'recording') return;
    this.voiceRecorder.cancel();
    this.endVoiceMessage();
  }

  private endVoiceMessage(): void {
    this.clearVoiceTimers();
    this.voiceState = 'idle';
    this.voiceRoomId = null;
    if (!this.destroyed) this.cdr.detectChanges();
  }

  private clearVoiceTimers(): void {
    clearInterval(this.voiceTickTimer);
    clearTimeout(this.voiceLimitTimer);
    this.voiceTickTimer = undefined;
    this.voiceLimitTimer = undefined;
  }

  /** Dispara el explorador de archivos nativo — sin ngx-dropzone, a propósito (CHAT.md sección 17). */
  triggerFileInput(): void {
    this.fileInput?.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;

    // Aviso inmediato, sin llegar a subir nada: por encima del límite el backend lo rechazaría igual
    // (y por encima de ~28,6 MB lo corta IIS antes de llegar a la Api, con un error de red genérico).
    if (file && file.size > MAX_ATTACHMENT_BYTES) {
      this.removeSelectedFile();
      this.cdr.detectChanges();
      this.helpers.showError(
        'Archivo demasiado grande',
        `El tamaño máximo permitido es de ${MAX_ATTACHMENT_MB} MB y el archivo seleccionado ocupa ${this.formatFileSize(file.size)}.`
      );
      return;
    }

    this.selectedFile = file;
    this.cdr.detectChanges();
  }

  removeSelectedFile(): void {
    this.selectedFile = null;
    if (this.fileInput) this.fileInput.nativeElement.value = '';
  }

  /** CHAT_REVISION.md C16: separador de día antes del primer mensaje de cada fecha. */
  isNewDay(index: number): boolean {
    if (index === 0) return true;
    return !this.isSameDay(new Date(this.messages[index - 1].CreationDate), new Date(this.messages[index].CreationDate));
  }

  dateSeparatorLabel(dateStr: string): string {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (this.isSameDay(date, today)) return 'Hoy';
    if (this.isSameDay(date, yesterday)) return 'Ayer';

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${day}/${month}`;
  }

  private isSameDay(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  }

  isImageFile(file?: ChatFileDTO): boolean {
    if (!file?.FileExtension) return false;
    return IMAGE_EXTENSIONS.includes(file.FileExtension.toLowerCase());
  }

  formatFileSize(bytes: number): string {
    if (!bytes || bytes <= 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
  }

  openPreviewModal(fileName: string, fileUri: string, fileSize: number): void {
    this.previewFileName = fileName;
    this.previewFileUri = fileUri;
    this.previewFileSize = fileSize;

    if (!this.previewModal) return;
    this.modalService.open(this.previewModal, { centered: true, size: 'xl' });
  }

  /**
   * Si el IdMessage ya está pintado, lo reemplaza por la versión nueva (una edición o un borrado
   * de un mensaje ya visible llegan por este mismo canal, con el mismo IdMessage); si no, lo añade
   * al final. Para el propio envío, el backend excluye al remitente de la difusión de su mensaje
   * nuevo (ver ChatRealtimePublisher.PublishMessageAsync), así que en ese caso concreto nunca
   * debería encontrar un duplicado — se deja como red de seguridad ante una reconexión de SignalR
   * que reenviara un evento ya recibido.
   *
   * Un mensaje de OTRA sala se ignora: las respuestas de enviar/editar/borrar no se cancelan al
   * cambiar de sala (tienen que completarse en el servidor), y sin este control, si llegan después
   * del cambio, no se encontrarían en la lista y se añadirían al final de la sala nueva como si
   * fueran suyos (CHAT_REVISION.md B9).
   */
  private upsertMessage(message: ChatMessageDTO): void {
    if (message.IdChat !== this.room?.IdChat) return;

    const index = this.messages.findIndex(m => m.IdMessage === message.IdMessage);

    if (index === -1) {
      this.messages = [...this.messages, message];
    } else {
      this.messages = this.messages.map((m, i) => (i === index ? message : m));
    }
  }

  private onMessageReceived(message: ChatMessageDTO): void {
    if (!this.room || message.IdChat !== this.room.IdChat) return;

    // Antes de upsertMessage(), que ya no distingue entre "no existía" (mensaje nuevo) y "ya
    // existía" (edición o borrado de un mensaje ya pintado).
    const isNewMessage = !this.messages.some(m => m.IdMessage === message.IdMessage);

    // Por este canal solo llegan mensajes nuevos (nunca con IsEdited/IsDeleted) y ediciones/borrados.
    // Una edición o borrado de un mensaje que no tengo cargado es de uno antiguo, fuera de la página
    // que tengo en pantalla: añadirlo al final lo pintaría desordenado como si fuera nuevo. Se
    // ignora — ya saldrá con su estado real al hacer scroll hacia arriba o al reabrir la sala.
    if (isNewMessage && (message.IsEdited || message.IsDeleted)) return;

    this.upsertMessage(message);

    // Una edición o un borrado se actualiza en su sitio y NUNCA mueve el scroll. Un mensaje nuevo solo
    // baja al final si el usuario ya estaba ahí (stickToBottom); si está leyendo más arriba no se le
    // arrastra, y se le muestra el botón "Nuevos mensajes" (CHAT_REVISION.md B10).
    const followNewMessage = isNewMessage && this.stickToBottom;
    if (isNewMessage && !followNewMessage) this.hasNewBelow = true;

    this.cdr.detectChanges();
    if (followNewMessage) this.scrollToBottom();

    if (!isNewMessage) return;

    // La sala está abierta y visible: el mensaje entrante se considera leído al instante. Solo para
    // mensajes realmente nuevos — repintar uno que ya tengo (edición o borrado) no es contenido
    // nuevo que leer, y marcarlo provocaría una llamada inútil.
    this.markAsRead(this.room.IdChat);
  }

  /**
   * Acuse de "visto por todos" de mis mensajes (evento propio, solo lo recibe el autor): pone el
   * check en azul en los que tenga pintados. Sin scroll ni markAsRead — no hay contenido nuevo.
   */
  private onMessagesReadByAll(event: ChatMessagesReadByAllDTO): void {
    if (!this.room || event.IdChat !== this.room.IdChat) return;

    const readIds = new Set(event.MessageIds);
    if (!this.messages.some(m => readIds.has(m.IdMessage) && !m.IsReadByAll)) return;

    this.messages = this.messages.map(m => (readIds.has(m.IdMessage) ? { ...m, IsReadByAll: true } : m));
    this.cdr.detectChanges();
  }

  startEdit(message: ChatMessageDTO): void {
    this.editingMessageId = message.IdMessage;
    this.editingText = message.Message;
  }

  cancelEdit(): void {
    this.editingMessageId = null;
    this.editingText = '';
  }

  saveEdit(message: ChatMessageDTO): void {
    const text = this.editingText.trim();
    if (!text) return;

    this.chatService.editMessage(message.IdMessage, text).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: updated => {
        this.upsertMessage(updated);
        this.cancelEdit();
        this.cdr.detectChanges();
        // Para que la lista de salas muestre el preview actualizado si era el último mensaje.
        this.chatService.notifyRoomsChanged();
      },
      error: err => this.helpers.showError('Error al editar el mensaje', getChatErrorDetail(err))
    });
  }

  confirmDeleteMessage(message: ChatMessageDTO): void {
    Swal.fire({
      title: '¿Borrar este mensaje?',
      text: 'Se mostrará como "Mensaje eliminado" en la conversación',
      icon: 'warning',
      showCancelButton: true,
      allowOutsideClick: false,
      confirmButtonText: 'Borrar <i class="fa-solid fa-trash-can"></i>',
      cancelButtonText: 'Cancelar <i class="fa-solid fa-xmark"></i>',
      reverseButtons: true,
      focusCancel: true,
      customClass: { confirmButton: 'btn btn-danger me-2', cancelButton: 'btn btn-outline-secondary' },
      buttonsStyling: false
    }).then(result => {
      if (!result.isConfirmed) return;

      this.chatService.deleteMessage(message.IdMessage).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: updated => {
          this.upsertMessage(updated);
          this.cdr.detectChanges();
          this.chatService.notifyRoomsChanged();
        },
        error: err => this.helpers.showError('Error al borrar el mensaje', getChatErrorDetail(err))
      });
    });
  }

  private markAsRead(idChat: string): void {
    this.chatService.markAsRead(idChat).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.chatService.notifyRoomsChanged());
  }

  private scrollToBottom(): void {
    const el = this.messagesContainer?.nativeElement;
    if (!el) return;

    this.stickToBottom = true;
    this.suppressScrollEvents = true;
    el.scrollTop = el.scrollHeight;
    this.reenableScrollEventsSoon();

    // Al final ya no queda nada "nuevo abajo". markForCheck porque se llama también desde callbacks
    // HTTP (p. ej. send()), fuera de un evento de plantilla, en un componente OnPush.
    if (this.hasNewBelow) {
      this.hasNewBelow = false;
      this.cdr.markForCheck();
    }
  }

  /** Botón "Nuevos mensajes": baja al final de la conversación. */
  goToNewMessages(): void {
    this.scrollToBottom();
  }

  /**
   * (load) de las imágenes adjuntas: al terminar de descargarse crecen y empujan el contenido. Si la
   * vista estaba pegada al final (ver stickToBottom), se vuelve a bajar para que la última imagen no
   * quede cortada; si el usuario había subido a leer el histórico, no se le mueve.
   */
  onImageLoaded(): void {
    if (this.stickToBottom) this.scrollToBottom();
  }

  /**
   * El "scroll" nativo que dispara nuestro propio `el.scrollTop = ...` no llega de forma síncrona
   * (el navegador lo encola), así que no basta con poner la bandera a false en la misma línea —
   * hay que dejar pasar un macrotask para que ese evento, si llega, se descarte en onScroll().
   */
  private reenableScrollEventsSoon(): void {
    setTimeout(() => {
      this.suppressScrollEvents = false;
    }, 0);
  }

  startEditTitle(): void {
    if (!this.room) return;
    this.editingTitleText = this.room.Title;
    this.editingTitle = true;
  }

  cancelEditTitle(): void {
    this.editingTitle = false;
    this.editingTitleText = '';
  }

  saveTitle(): void {
    const title = this.editingTitleText.trim();
    if (!title || this.savingTitle || !this.room) return;

    this.savingTitle = true;
    this.chatService.renameRoom(this.room.IdChat, title).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.savingTitle = false;
        this.cancelEditTitle();
        this.cdr.detectChanges();
        // La cabecera se actualiza sola: ChatModalComponent sustituye `room` por su versión fresca
        // en cuanto llega la recarga (mismo mecanismo que saveEdit/leaveRoom).
        this.chatService.notifyRoomsChanged();
      },
      error: err => {
        this.savingTitle = false;
        this.helpers.showError('Error al cambiar el nombre de la sala', getChatErrorDetail(err));
        this.cdr.detectChanges();
      }
    });
  }

  openManageMembers(): void {
    if (!this.room) return;

    const modalRef = this.modalService.open(ChatManageMembersModalComponent, { centered: true });
    modalRef.componentInstance.idChat = this.room.IdChat;
    modalRef.componentInstance.isAdmin = this.room.IsAdmin;

    modalRef.closed.subscribe((changed: boolean) => {
      if (changed) this.chatService.notifyRoomsChanged();
    });
    // CHAT_REVISION.md C17: cerrar con ESC o clic fuera dispara "dismissed", no "closed" — el flag
    // "changed" del componente nunca llega por ahí. No sabemos si hubo cambios, así que se refresca
    // siempre (debounced y sin coste si no había nada nuevo).
    modalRef.dismissed.subscribe(() => this.chatService.notifyRoomsChanged());
  }

  leaveRoom(): void {
    if (!this.room) return;

    Swal.fire({
      title: '¿Quieres salir de esta sala?',
      text: 'Seguirás viendo la conversación en modo solo lectura, pero no podrás escribir en ella',
      icon: 'warning',
      showCancelButton: true,
      allowOutsideClick: false,
      confirmButtonText: 'Salir <i class="fa-solid fa-right-from-bracket"></i>',
      cancelButtonText: 'Cancelar <i class="fa-solid fa-xmark"></i>',
      reverseButtons: true,
      focusCancel: true,
      customClass: { confirmButton: 'btn btn-danger me-2', cancelButton: 'btn btn-outline-secondary' },
      buttonsStyling: false
    }).then(result => {
      if (!result.isConfirmed || !this.room) return;

      this.chatService.leaveRoom(this.room.IdChat).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          this.chatService.notifyRoomsChanged();
          this.roomLeft.emit();
        },
        error: err => this.helpers.showError('Error al salir de la sala', getChatErrorDetail(err))
      });
    });
  }
}
